import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { PaginatedMetadata } from '@/types'
import { PAGE_SIZE_OPTIONS, showingRangeLabel } from '@/lib/listFetch'

interface ListPaginationProps {
  metadata: PaginatedMetadata | undefined
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  pageSizeOptions?: readonly number[]
  pageSizeLabel?: string
  className?: string
  showShowingRange?: boolean
  showSummary?: boolean
  perPagePosition?: 'left' | 'right'
  typographyVariant?: 'default' | 'activityPreview'
}

function buildPageList(current: number, last: number): Array<number | '...'> {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1)

  if (current <= 3) return [1, 2, 3, '...', last - 1, last]
  if (current >= last - 2) return [1, 2, '...', last - 2, last - 1, last]

  return [1, '...', current - 1, current, current + 1, '...', last]
}

const pageBtnClass =
  'inline-flex min-w-8 items-center justify-center rounded-lg px-3 py-2 text-sm font-medium leading-[1.4]'

const perPageSelectTriggerClass =
  'h-10 w-auto shrink-0 gap-2 rounded-xl border border-[#e7e9ef] bg-white py-0 pl-4 pr-3 text-base font-medium leading-none text-[#171a20] focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:ring-0 [&>span]:font-medium [&>span>span]:font-medium [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:opacity-100'

// Figma list footer (e.g. 987:30254, 1030:38188, 2881:33987) — Previous / pages / Next + Per page.
export function ListPagination({
  metadata,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  pageSizeLabel,
  className,
  showShowingRange = true,
  showSummary = true,
  perPagePosition = 'right',
  typographyVariant = 'default',
}: ListPaginationProps) {
  const { t } = useTranslation('shell')
  const lastPage = metadata?.last_page ?? 1
  const currentPage = metadata?.current_page ?? page
  const atFirst = currentPage <= 1
  const atLast = currentPage >= lastPage
  const pages = buildPageList(currentPage, lastPage)
  const activityPreviewTypography = typographyVariant === 'activityPreview'
  const perPageControl = (
    <div className='flex shrink-0 items-center gap-2'>
      <span
        className={cn(
          'text-sm leading-[1.4] text-[#171a20]',
          activityPreviewTypography ? 'font-normal' : 'font-medium'
        )}
      >
        {pageSizeLabel ?? t('pagination.perPage')}
      </span>
      <Select
        value={String(pageSize)}
        onValueChange={v => onPageSizeChange(Number(v))}
      >
        <SelectTrigger
          className={cn(
            perPageSelectTriggerClass,
            activityPreviewTypography &&
              'text-sm font-normal [&>span]:font-normal [&>span>span]:font-normal'
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {pageSizeOptions.map(opt => (
            <SelectItem key={opt} value={String(opt)}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-between gap-3 border-t border-[#e7e9ef] bg-white py-2 pl-2 pr-4 sm:flex-row',
        className
      )}
    >
      {perPagePosition === 'left' && perPageControl}

      {showSummary ? (
        <p
          className={cn(
            'shrink-0 text-sm text-muted-foreground',
            !showShowingRange && 'hidden'
          )}
        >
          {showingRangeLabel(metadata)}
        </p>
      ) : null}

      <div
        className={cn(
          'flex flex-wrap items-center gap-2',
          showSummary ? 'justify-center' : 'justify-start'
        )}
      >
        <button
          type='button'
          disabled={atFirst}
          onClick={() => onPageChange(currentPage - 1)}
          className={cn(
            pageBtnClass,
            activityPreviewTypography && 'text-base font-normal',
            'gap-2 text-[#3d434e] disabled:opacity-50',
            !atFirst && 'text-[#171a20] hover:bg-accent'
          )}
        >
          <ArrowLeft className='h-4 w-4 shrink-0' />
          {t('pagination.previous')}
        </button>

        <div className='flex items-center gap-2'>
          {pages.map((p, i) =>
            p === '...' ? (
              <span
                key={`ellipsis-${i}`}
                className={cn(
                  'px-4 py-2 font-medium leading-[1.4]',
                  activityPreviewTypography
                    ? 'text-xs text-black'
                    : 'text-base text-foreground'
                )}
              >
                ...
              </span>
            ) : (
              <button
                key={p}
                type='button'
                onClick={() => onPageChange(p)}
                aria-current={p === currentPage ? 'page' : undefined}
                className={cn(
                  pageBtnClass,
                  activityPreviewTypography && 'text-xs font-medium',
                  p === currentPage
                    ? 'bg-[#fff1ea] text-[#ff5a1f]'
                    : cn(
                        'font-medium hover:bg-accent',
                        activityPreviewTypography
                          ? 'text-[#171a20]'
                          : 'text-[#1a1b1e]'
                      )
                )}
              >
                {p}
              </button>
            )
          )}
        </div>

        <button
          type='button'
          disabled={atLast}
          onClick={() => onPageChange(currentPage + 1)}
          className={cn(
            pageBtnClass,
            'gap-2 font-medium text-[#171a20] disabled:opacity-50',
            !atLast && 'hover:bg-accent'
          )}
        >
          {t('pagination.next')}
          <ArrowRight className='h-4 w-4 shrink-0' />
        </button>
      </div>

      {perPagePosition === 'right' && perPageControl}
    </div>
  )
}
